/** Expected CSV columns (header row). Extra columns ignored. */
export const CANDIDATE_CSV_HEADERS = [
  'full_name',
  'email',
  'phone',
  'linkedin_url',
  'current_company',
  'current_title',
  'location',
  'source_channel',
  'years_exp',
  'parsed_skills',
  'notes',
  'tags',
] as const;

export type CandidateCsvRow = {
  full_name: string;
  email: string;
  phone: string | null;
  linkedin_url: string | null;
  current_company: string | null;
  current_title: string | null;
  location: string | null;
  source_channel: string | null;
  years_exp: number | null;
  parsed_skills: string[];
  notes: string | null;
  tags: string[];
};

function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (!inQuotes && c === ',') {
      out.push(cur.trim());
      cur = '';
      continue;
    }
    cur += c;
  }
  out.push(cur.trim());
  return out;
}

function splitSkills(raw: string): string[] {
  if (!raw) return [];
  return raw
    .split(/[;|]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function splitTags(raw: string): string[] {
  if (!raw) return [];
  return raw
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function parseCandidatesCsv(text: string): { rows: CandidateCsvRow[]; errors: string[] } {
  const errors: string[] = [];
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) {
    errors.push('CSV must include a header row and at least one data row.');
    return { rows: [], errors };
  }

  const headerCells = parseCsvLine(lines[0]).map((h) => h.replace(/^\ufeff/, '').toLowerCase().trim());
  const index: Record<string, number> = {};
  headerCells.forEach((h, i) => {
    index[h] = i;
  });

  const required = ['full_name', 'email'];
  for (const r of required) {
    if (index[r] === undefined) errors.push(`Missing required column: ${r}`);
  }
  if (errors.length) return { rows: [], errors };

  const rows: CandidateCsvRow[] = [];
  for (let li = 1; li < lines.length; li++) {
    const cells = parseCsvLine(lines[li]);
    const get = (key: string) => {
      const i = index[key];
      return i !== undefined ? (cells[i] ?? '').trim() : '';
    };

    const full_name = get('full_name');
    const email = get('email');
    if (!full_name && !email) continue;
    if (!full_name || !email) {
      errors.push(`Row ${li + 1}: full_name and email are required.`);
      continue;
    }

    const yearsRaw = get('years_exp');
    const years_exp = yearsRaw ? parseInt(yearsRaw, 10) : null;
    if (yearsRaw && Number.isNaN(years_exp)) {
      errors.push(`Row ${li + 1}: invalid years_exp "${yearsRaw}"`);
      continue;
    }

    rows.push({
      full_name,
      email,
      phone: get('phone') || null,
      linkedin_url: get('linkedin_url') || null,
      current_company: get('current_company') || null,
      current_title: get('current_title') || null,
      location: get('location') || null,
      source_channel: get('source_channel') || null,
      years_exp,
      parsed_skills: splitSkills(get('parsed_skills')),
      notes: get('notes') || null,
      tags: splitTags(get('tags')),
    });
  }

  return { rows, errors };
}
